import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Modal,
  Box,
  Typography,
  IconButton,
  Divider,
  TextField,
  FormControl,
  FormLabel,
  Button,
  CircularProgress,
} from "@mui/material";

import { CloseIcon } from "../../../../../../../assets/icons/MUI/UIComponentsIcons";
import { useUpdateServiceProfileMutation } from "../../../../../../User/api/mutations/useUpdateServiceProfileMutation";

const AddServiceModal = ({
  open,
  onClose,
  userId,
  overview = "",
}: {
  open: boolean;
  onClose: () => void;
  userId?: string;
  overview?: string;
}) => {
  const { t } = useTranslation("profiles", {
    keyPrefix: "goService",
  });
  const [serviceOverview, setServiceOverview] = useState("");
  const handleOverviewChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setServiceOverview(event.target.value);
  };
  const { mutate: updateServiceOverview, isPending: isUpdatingServiceOverview } = useUpdateServiceProfileMutation();

  useEffect(() => {
    setServiceOverview(overview);
  }, [overview]);

  const handleSaveOverview = () => {
    if (!userId) return;
    updateServiceOverview(
      {
        userId: userId,
        payload: { overview: serviceOverview },
      },
      {
        onSuccess: () => {
          onClose();
        },
      }
    );
  };

  return (
    <Modal open={open} onClose={onClose}>
      <Box sx={styles.container}>
        <Box sx={styles.header}>
          <Typography sx={styles.title}>{t("addOverview")}</Typography>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
        <Divider flexItem orientation="horizontal" />
        <Box sx={styles.formWrapper}>
          <Typography sx={styles.formTitle}>{t("serviceOverview")}</Typography>
          <FormControl fullWidth sx={styles.formControl}>
            <FormLabel sx={styles.formLabel}>{t("describeYourServices")}</FormLabel>
            <TextField fullWidth multiline rows={5} value={serviceOverview} onChange={handleOverviewChange} />
          </FormControl>
        </Box>
        <Divider flexItem orientation="horizontal" />
        <Box sx={styles.footer}>
          <Button variant="outlined-md" onClick={onClose}>
            {t("cancel")}
          </Button>
          <Button
            variant="primary-md"
            disabled={!serviceOverview || isUpdatingServiceOverview}
            onClick={handleSaveOverview}
          >
            {isUpdatingServiceOverview ? <CircularProgress size={20} /> : t("save")}
          </Button>
        </Box>
      </Box>
    </Modal>
  );
};

export default AddServiceModal;

const styles = {
  container: {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    width: { xs: "90%", md: "800px" },
    height: "fit-content",
    borderRadius: "16px",
    background: "#FFF",
    boxShadow: "0 4px 50px 0 rgba(0, 0, 0, 0.08), 0 4px 6px 0 rgba(0, 0, 0, 0.05)",
    display: "flex",
    flexDirection: "column",
    gap: "24px",
  },
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: "24px 32px 0",
  },
  title: {
    fontFamily: "Montserrat",
    color: "var(--Neutral-neutral-900, #0B0920)",
    fontSize: "18px",
    fontStyle: "normal",
    fontWeight: 600,
    lineHeight: "28px",
  },
  formWrapper: {
    px: "32px",
    display: "flex",
    flexDirection: "column",
    gap: "16px",
  },
  formTitle: {
    fontFamily: "Montserrat",
    color: "var(--Dark---1000, #111)",
    fontSize: "18px",
    fontStyle: "normal",
    fontWeight: 600,
    lineHeight: "28px",
  },
  formControl: {
    display: "flex",
    flexDirection: "column",
    gap: "6px",
  },
  formLabel: {
    fontFamily: "Montserrat",
    color: "var(--Gray-700, #344054)",
    fontSize: "14px",
    fontStyle: "normal",
    fontWeight: 500,
    lineHeight: "20px",
  },
  footer: {
    display: "flex",
    justifyContent: "flex-end",
    gap: "12px",
    padding: "0 32px 24px",
  },
};
